import '../operations.css';
import './simulations.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, CircleStop, Trash2, Eye, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { campaignsApi } from '../../api/campaigns';
import type { CampaignWithRecurrence } from '../../api/campaigns';

export function CampaignList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    campaign: CampaignWithRecurrence | null;
  }>({
    open: false,
    campaign: null,
  });
  const [stopCampaign, setStopCampaign] = useState<CampaignWithRecurrence | null>(null);

  const { data: campaigns, isLoading, isError, refetch } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const res = await campaignsApi.getAll();
      return res.data;
    },
  });

  const summaries = useQuery({ queryKey: ['campaigns-summary'], queryFn: async () => (await campaignsApi.getSummary()).data.campaigns });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => campaignsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns-summary'] });
      setDeleteModal({ open: false, campaign: null });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: number) => campaignsApi.stop(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns-summary'] });
      setStopCampaign(null);
    },
  });

  const [statusFilter, setStatusFilter] = useState('');
  const filteredCampaigns = campaigns?.filter(campaign => !statusFilter || campaign.status === statusFilter).map(campaign => ({...campaign, stats: summaries.data?.find(summary => summary.id === campaign.id)?.stats ?? campaign.stats}));

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (campaign: CampaignWithRecurrence) => (
        <div>
          <p className="font-medium">{campaign.name}</p>
          <p className="text-sm text-gray-500">{campaign.url}</p>
          {campaign.recurring && (
            <span className="badge inline-flex items-center gap-1 mt-1 bg-indigo-50 text-indigo-700">
              <RefreshCw className="w-3 h-3" />
              Recurring{campaign.recurrence_interval ? ` · ${campaign.recurrence_interval}` : ''}
            </span>
          )}
          {!campaign.recurring && (campaign.parent_campaign_id ?? 0) > 0 && (
            <span className="badge badge-gray mt-1">Occurrence</span>
          )}
          {(campaign.template_ids?.length ?? 0) > 1 && (
            <span className="badge badge-gray mt-1">Multi-variant</span>
          )}
          {campaign.no_whitelist && (
            <span className="badge mt-1 bg-amber-50 text-amber-700">No whitelist</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (campaign: CampaignWithRecurrence) => (
        <span className={`badge ${getStatusBadge(campaign.status)}`}>
          {campaign.status}
        </span>
      ),
    },
    {
      key: 'stats',
      header: 'Results',
      render: (campaign: CampaignWithRecurrence) => (
        <div className="text-sm">
          <span className="text-green-600">{campaign.stats?.sent ?? 'Unavailable'} sent</span>
          <span className="mx-1">|</span>
          <span className="text-blue-600">{campaign.stats?.clicked ?? 'Unavailable'} clicked</span>
          <span className="mx-1">|</span>
          <span className="text-red-600">{campaign.stats?.submitted_data ?? 'Unavailable'} submitted</span>
        </div>
      ),
    },
    {
      key: 'launch_date',
      header: 'Launch Date',
      render: (campaign: CampaignWithRecurrence) => (
        <div className="text-sm text-gray-500">
          <span>{new Date(campaign.launch_date).toLocaleDateString()}</span>
          {campaign.recurring && isRealDate(campaign.next_run_at) && (
            <p className="text-xs text-indigo-600">
              Next run: {new Date(campaign.next_run_at!).toLocaleDateString()}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (campaign: CampaignWithRecurrence) => (
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/campaigns/${campaign.id}`);
            }}
            className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="View Results"
          >
            <Eye className="w-4 h-4" />
          </button>
          {campaign.status === 'In progress' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
              setStopCampaign(campaign);
              }}
              className="p-2 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
              title="Stop Campaign"
            >
              <CircleStop className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ open: true, campaign });
            }}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="operations-eyebrow">Simulation workspace</p>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-gray-500 mt-1">Track delivery, recipient exposure, and recurring simulation series</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            loading={isLoading || summaries.isFetching}
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
              void queryClient.invalidateQueries({ queryKey: ['campaigns-summary'] });
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => navigate('/campaigns/new')}>
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      {campaigns && <div className="sim-outcomes"><div><span>All simulations</span><strong>{campaigns.length}</strong></div><div><span>In progress</span><strong>{campaigns.filter(c => c.status === 'In progress').length}</strong></div><div><span>Queued</span><strong>{campaigns.filter(c => c.status === 'Queued').length}</strong></div><div><span>Recurring series</span><strong>{campaigns.filter(c => c.recurring).length}</strong></div></div>}
      <Card>
        <div className="flex flex-wrap justify-between gap-4 mb-6"><h2 className="text-lg font-semibold">Simulation register</h2><label>Status <select className="input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}><option value="">All statuses</option>{['Created','Queued','In progress','Emails Sent','Completed'].map(status => <option key={status}>{status}</option>)}</select></label></div>
        {summaries.isError && <p className="sim-muted">Outcome counts could not be refreshed. Available campaign records remain visible.</p>}
        {(deleteMutation.isError || completeMutation.isError) && <p role="alert" className="sim-error">Campaign action failed. Try again.</p>}
        {isError ? <div className="sim-error" role="alert">Could not load campaigns.<Button variant="secondary" onClick={() => {void refetch();}}>Retry</Button></div> : isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            data={filteredCampaigns ?? []}
            keyExtractor={(c) => c.id}
            onRowClick={(c) => navigate(`/campaigns/${c.id}`)}
            emptyMessage="No campaigns yet. Create your first campaign to get started."
          />
        )}
      </Card>

      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, campaign: null })}
        title="Delete Campaign"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete "{deleteModal.campaign?.name}"? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => setDeleteModal({ open: false, campaign: null })}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={deleteMutation.isPending}
            onClick={() => deleteModal.campaign && deleteMutation.mutate(deleteModal.campaign.id)}
          >
            Delete
          </Button>
        </div>
      </Modal>
      <Modal isOpen={stopCampaign !== null} onClose={() => setStopCampaign(null)} title="Stop campaign">
        <p className="text-gray-600 mb-5">Stop future scheduled sends for &quot;{stopCampaign?.name}&quot;? Messages already submitted and recorded results will remain.</p>
        {completeMutation.isError && <p role="alert" className="text-red-700 mb-4">Could not stop the campaign. Refresh and try again.</p>}
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setStopCampaign(null)}>Cancel</Button>
          <Button variant="danger" loading={completeMutation.isPending} onClick={() => { if (stopCampaign) completeMutation.mutate(stopCampaign.id); }}>Stop campaign</Button>
        </div>
      </Modal>
    </div>
  );
}

// isRealDate guards against the Go zero time ("0001-01-01T00:00:00Z"), which
// the backend emits for recurring series with no further scheduled runs.
function isRealDate(value?: string | null): boolean {
  if (!value) return false;
  const year = new Date(value).getFullYear();
  return Number.isFinite(year) && year > 2000;
}

function getStatusBadge(status: string): string {
  switch (status) {
    case 'Completed':
      return 'badge-green';
    case 'In progress':
      return 'badge-yellow';
    case 'Queued':
      return 'badge-gray';
    case 'Emails Sent':
      return 'badge-green';
    default:
      return 'badge-gray';
  }
}
